import { Button } from "antd";
import { EditFilled, LeftOutlined, RightOutlined, ExpandOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useContext, useRef, useState } from "react";
import styled from "styled-components";
import ViewerDisplay from "./ViewerDisplay";
import SlidePathologyBadge from "./SlidePathologyBadge";
import { GrossImage, Photo, Post } from "../../api/postAPI";
import { Slide } from "../../api/slideAPI";
import { ViewerContext } from "../../page/Viewer";
import { patchUserCustomSetting } from "../../api/user";
import { PostVideo } from "../../api/videoAPI";

interface Props {
  imageList: Photo[];
  slides: Slide[];
  grossImageList: GrossImage[];
  currentIndex: number;
  isSlideFile: boolean;
  isGrossFile: boolean;
  currentImage: Photo | null;
  currentSlide: Slide;
  currentGrossImage: GrossImage | null;
  currentPost: Post;
  hasEditAuth: boolean;
  activeCaseId?: number | null;       // 추가: 현재 활성 case (null 이면 전체)
  layoutMode?: "vertical" | "horizontal";
  hideResizeDivider?: boolean;        // 뷰어 높이 조절 divider 숨김 (자문 페이지 등)
  onPrev: () => void;
  onNext: () => void;
  onEditImage: () => void;
  onEditGrossImage: () => void;
  /** 동영상 탭 */
  videoList?: PostVideo[];
  isVideoFile?: boolean;
  currentVideo?: PostVideo | null;
}

const PostViewerSection = ({
  imageList,
  slides,
  grossImageList,
  currentIndex,
  isSlideFile,
  isGrossFile,
  currentImage,
  currentSlide,
  currentGrossImage,
  currentPost,
  hasEditAuth,
  activeCaseId = null,
  layoutMode = "horizontal",
  hideResizeDivider = false,
  onPrev,
  onNext,
  onEditImage,
  onEditGrossImage,
  videoList = [],
  isVideoFile = false,
  currentVideo = null,
}: Props) => {
  const viewerContext = useContext(ViewerContext);
  const [searchParams] = useSearchParams();

  const [viewerHeight, setViewerHeight] = useState<number | null>(null);
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  const handleDividerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    startYRef.current = e.clientY;
    // 아직 드래그 전(뷰어가 남는 공간을 채워 늘어난 상태)이면 실제 렌더 높이를 시작점으로 사용
    startHeightRef.current =
      viewerHeight ?? viewerRef.current?.getBoundingClientRect().height ?? window.innerHeight * 0.55;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const minH = window.innerHeight * 0.55;
      const delta = ev.clientY - startYRef.current;
      setViewerHeight(Math.max(minH, startHeightRef.current + delta));
    };

    const onMouseUp = () => {
      isDraggingRef.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  //뷰어 페이지 URL. media 로 종류를 알리고 종류별 id 쿼리(slideId / photoId / grossPhotoId) 적용
  //slideName / slideIndex 는 브레드크럼 라벨용 (종류와 무관)
  const getViewerUrl = (media: "slide" | "image" | "gross" = "slide") => {
    const params = new URLSearchParams(searchParams);
    params.delete('slideId');
    params.delete('photoId');
    params.delete('grossPhotoId');
    if (media === 'slide') {
      params.delete('media');
      params.set('slideId', currentSlide?.slideId?.toString() || '');
      params.set('slideName', currentSlide?.slidename || '');
    } else if (media === 'image') {
      params.set('media', 'image');
      params.set('photoId', currentImage?.photoId?.toString() || '');
      params.set('slideName', currentImage?.imageName || '');
    } else {
      params.set('media', 'gross');
      params.set('grossPhotoId', currentGrossImage?.grossPhotoId?.toString() || '');
      params.set('slideName', currentGrossImage?.caption || '');
    }
    params.set('slideIndex', (currentIndex + 1).toString());
    if (currentPost?.title) {
      params.set('title', currentPost.title);
    }
    if (activeCaseId !== null && activeCaseId !== undefined) {
      params.set('postCaseId', String(activeCaseId));
    } else {
      params.delete('postCaseId');
    }
    params.set('newTab', 'true');
    return `/viewer/${currentPost?.id}?${params.toString()}`;
  };

  const handleOpenViewer2 = (media: "slide" | "image" | "gross") => async (e: React.MouseEvent) => {
    if (e.button === 1 || e.ctrlKey || e.metaKey) return;
    e.preventDefault();
    if (viewerContext?.userCustomSet) {
      await patchUserCustomSetting(viewerContext.userCustomSet);
    }
    window.open(getViewerUrl(media), '_blank');
  };

  const showSlideInfo = !isVideoFile && isSlideFile && slides.length > 0;
  const showImageInfo = !isVideoFile && !isSlideFile && !isGrossFile && imageList.length > 0;
  const showGrossInfo = !isVideoFile && isGrossFile && grossImageList.length > 0;
  const showVideoInfo = isVideoFile && videoList.length > 0;

  return (
    <>
      <ViewerContainer
        ref={viewerRef}
        $layoutMode={layoutMode}
        style={viewerHeight != null ? { height: `${viewerHeight}px`, flexGrow: 0, flexShrink: 0 } : undefined}
      >
        <ViewerDisplay
          imageList={imageList}
          slideList={slides}
          grossImageList={grossImageList}
          currentIndex={currentIndex}
          isSlideFile={isSlideFile}
          isGrossFile={isGrossFile}
          currentImage={currentImage}
          currentSlide={currentSlide}
          currentGrossImage={currentGrossImage}
          currentPost={currentPost}
          isAnno={true}
          isVideoFile={isVideoFile}
          currentVideo={currentVideo}
        />
      </ViewerContainer>

      {!hideResizeDivider && <ResizeDivider onMouseDown={handleDividerMouseDown} />}

      <NavigationContainer>
        <NavigationContent>
          <NavButton
            shape="circle"
            icon={<LeftOutlined />}
            onClick={onPrev}
            size="middle"
          />

          <MediaInfoContainer>
            {showSlideInfo && (
              <MediaRow>
                <MetaContainer>
                  <TitleRow>
                    <Title>{currentSlide?.slidename || "Untitled"}</Title>
                    {/* 병리번호 배지 (관리자 전용, hover 시 memo) */}
                    <SlidePathologyBadge slideId={currentSlide?.slideId} />
                  </TitleRow>
                  {currentSlide?.zindanName && (
                    <Diagnosis>{currentSlide.zindanName}</Diagnosis>
                  )}
                </MetaContainer>
                <ViewerButton
                  type="default"
                  href={getViewerUrl('slide')}
                  target="_blank"
                  onClick={handleOpenViewer2('slide')}
                  icon={<ExpandOutlined />}
                >
                  Open in Viewer
                </ViewerButton>
              </MediaRow>
            )}

            {showImageInfo && (
              <MediaRow>
                <MetaContainer>
                  <Title>{currentImage?.imageName || "Untitled"}</Title>
                  {currentImage?.zindanName && (
                    <Diagnosis>{currentImage.zindanName}</Diagnosis>
                  )}
                </MetaContainer>
                <ViewerButton
                  type="default"
                  href={getViewerUrl('image')}
                  target="_blank"
                  onClick={handleOpenViewer2('image')}
                  icon={<ExpandOutlined />}
                >
                  Open in Viewer
                </ViewerButton>
              </MediaRow>
            )}

            {showVideoInfo && (
              <MediaRow>
                <MetaContainer>
                  <Title>{currentVideo?.videoName || "Untitled"}</Title>
                </MetaContainer>
              </MediaRow>
            )}

            {showGrossInfo && (
              <MediaRow>
                <MetaContainer>
                  <Title>{currentGrossImage?.caption || "Untitled"}</Title>
                  {currentGrossImage?.zindanName && (
                    <Diagnosis>{currentGrossImage.zindanName}</Diagnosis>
                  )}
                </MetaContainer>
                <ViewerButton
                  type="default"
                  href={getViewerUrl('gross')}
                  target="_blank"
                  onClick={handleOpenViewer2('gross')}
                  icon={<ExpandOutlined />}
                >
                  Open in Viewer
                </ViewerButton>
              </MediaRow>
            )}
          </MediaInfoContainer>

          <NavButton
            shape="circle"
            icon={<RightOutlined />}
            onClick={onNext}
            size="middle"
          />
        </NavigationContent>
      </NavigationContainer>
    </>
  );
};

const ViewerContainer = styled.div<{ $layoutMode: "vertical" | "horizontal" }>`
  background-color: #ffffff;
  border: 1px solid #e8e8e8;
  border-right-width: 0;
  border-left-width: 0;
  border-top-width: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $layoutMode }) =>
    $layoutMode === "horizontal"
      ? `
    flex: 1 1 auto;
    min-height: 0;
  `
      : `
    flex: 0 0 auto;
    height: 55vh;
  `}
`;

const ResizeDivider = styled.div`
  height: 8px;
  background: #f5f5f5;
  border-top: 1px solid #e8e8e8;
  border-bottom: 1px solid #e8e8e8;
  cursor: row-resize;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;

  &::after {
    content: '';
    width: 36px;
    height: 3px;
    border-radius: 2px;
    background: #d9d9d9;
  }

  &:hover {
    background: #ebebeb;
    &::after {
      background: #bfbfbf;
    }
  }
`;

const NavigationContainer = styled.div`
  background-color: #fff;
  border-bottom: 1px solid #e8e8e8;
  padding: 8px 20px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
`;

const NavigationContent = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  max-width: 1200px;
  width: 100%;
`;

const NavButton = styled(Button)`
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  flex-shrink: 0;
  color: rgba(30, 46, 58, 0.85) !important;
  border: none !important;
  transition: all 0.2s !important;

  &:hover {
    background-color: rgba(30, 46, 58, 1) !important;
    color: #fff !important;
    transform: scale(1.05);
  }
`;

const MediaInfoContainer = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

// slide/gross/image 공통 행 레이아웃: 왼쪽 메타(제목+진단) + 오른쪽 액션
const MediaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MetaContainer = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #262626;
  line-height: 1.4;
  word-break: break-word;
  max-width: 100%;
`;

const Diagnosis = styled.div`
  color: #8c8c8c;
  font-size: 0.8rem;
  line-height: 1.4;
  word-break: break-word;
  max-width: 100%;
`;

const ViewerButton = styled(Button)`
  flex-shrink: 0;
  height: auto !important;
  padding: 6px 14px !important;
  border-radius: 6px;
  font-size: 0.85rem !important;
  font-weight: 500;
  color: #1890ff !important;
  border-color: #91d5ff !important;
  box-shadow: none !important;
  transition: all 0.2s !important;

  &:hover {
    color: #40a9ff !important;
    border-color: #40a9ff !important;
    background-color: #f0f9ff !important;
  }
`;

export default PostViewerSection;